// 📄 tablaZService.ts
// Catálogo de Tablas SAP personalizadas (desarrollos Z) por proyecto — ver
// ConfiguracionTablasZ.tsx (Configuración > Desarrollo Z). Mismo patrón de
// acceso EXACTO que badiZService.ts/transaccionZService.ts: Firestore
// DIRECTO desde el cliente (colección projects/{projectId}/tablasZ),
// protegido por firestore.rules. Proceso INDEPENDIENTE del catálogo
// GLOBAL de tablas estándar (ver catalogoTablasService.ts) — acá son
// registros propios de ESTE proyecto, solo su equipo los ve/edita; el
// catálogo global lo gestiona un Admin y lo ve TODA la plataforma.
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { SolucionBadi } from "./catalogoBadisService.ts";

// Campo de la tabla — nombre técnico + una descripción/ejemplo de
// referencia (ej. "MSGTYP" — "Tipo de mensaje, ej. 'E' = Error"). Fuente
// única del tipo — catalogoTablasService.ts lo reusa tal cual, mismo shape
// para el catálogo global y el de cada proyecto.
export interface CampoTabla {
  nombre: string;
  descripcion: string;
}

export interface TablaZData {
  nombreTecnico: string;
  transaccion: string;
  descripcion: string;
  solucion: SolucionBadi;
  campos: CampoTabla[];
}

export interface TablaZ extends TablaZData {
  id: string;
  creadoPor: string;
  creadoEn: any;
  actualizadoEn: any;
}

const tablasZRef = (projectId: string) => collection(db, "projects", projectId, "tablasZ");
const tablaZRef = (projectId: string, id: string) => doc(db, "projects", projectId, "tablasZ", id);

export const getTablasZ = async (projectId: string): Promise<TablaZ[]> => {
  const snap = await getDocs(tablasZRef(projectId));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<TablaZ, "id">) }))
    .sort((a, b) => a.nombreTecnico.localeCompare(b.nombreTecnico));
};

const limpiarCampos = (campos: CampoTabla[]): CampoTabla[] =>
  campos.filter((c) => c.nombre.trim()).map((c) => ({ nombre: c.nombre.trim(), descripcion: c.descripcion.trim() }));

export const crearTablaZ = async (projectId: string, data: TablaZData, creadoPor: string): Promise<string> => {
  if (!data.nombreTecnico.trim()) throw new Error("El nombre técnico de la tabla es obligatorio.");

  const ref = doc(tablasZRef(projectId));
  await setDoc(ref, {
    ...data,
    nombreTecnico: data.nombreTecnico.trim(),
    transaccion: data.transaccion.trim(),
    descripcion: data.descripcion.trim(),
    campos: limpiarCampos(data.campos),
    creadoPor,
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp()
  });
  return ref.id;
};

export const actualizarTablaZ = async (projectId: string, id: string, data: TablaZData): Promise<void> => {
  if (!data.nombreTecnico.trim()) throw new Error("El nombre técnico de la tabla es obligatorio.");

  await updateDoc(tablaZRef(projectId, id), {
    ...data,
    nombreTecnico: data.nombreTecnico.trim(),
    transaccion: data.transaccion.trim(),
    descripcion: data.descripcion.trim(),
    campos: limpiarCampos(data.campos),
    actualizadoEn: serverTimestamp()
  });
};

export const eliminarTablaZ = async (projectId: string, id: string): Promise<void> => {
  await deleteDoc(tablaZRef(projectId, id));
};
